import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useConsent } from '@/lib/consent';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { initAnalytics, unloadAnalytics } from '@/lib/analytics';
import { useLanguage } from '@/contexts/LanguageContext';

export function ConsentBanner() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  
  console.log('[ConsentBanner] Rendered', { isVisible });

  // Safe consent hook usage with error handling
  let consentHook;
  try {
    consentHook = useConsent();
  } catch (error) {
    console.error('Failed to initialize consent hook:', error);
    return null;
  }
  
  const { setConsent, dismissBanner } = consentHook;
  const { success } = useHapticFeedback();

  const handleAccept = () => {
    console.log('[ConsentBanner] User clicked Accept');
    setConsent({ analytics: true, bannerDismissed: true });
    initAnalytics();
    success();
    setIsVisible(false);
    console.log('[ConsentBanner] Consent accepted, banner hidden');
  };

  const handleReject = () => {
    console.log('[ConsentBanner] User clicked Reject');
    setConsent({ analytics: false, bannerDismissed: true });
    unloadAnalytics();
    success();
    setIsVisible(false);
    console.log('[ConsentBanner] Consent rejected, banner hidden');
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe bg-background/80 backdrop-blur-sm border-t"
      role="dialog"
      aria-label={t('aria.cookieConsent')}
    >
      <Card className="max-w-2xl mx-auto p-4 shadow-lg">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold mb-1">{t('consent.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('consent.description')}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleAccept}
              className="flex-1"
              variant="default"
              aria-label={t('aria.acceptCookies')}
            >
              {t('consent.accept')}
            </Button>
            <Button
              onClick={handleReject}
              className="flex-1"
              variant="outline"
              aria-label={t('aria.rejectCookies')}
            >
              {t('consent.reject')}
            </Button>
            <Button
              onClick={() => navigate('/settings/privacy-analytics')}
              variant="ghost"
              className="sm:w-auto"
              aria-label={t('aria.learnMorePrivacy')}
            >
              {t('consent.learnMore')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
